import { LegalLayout, CLINIC } from "../components/LegalLayout";
import { Button } from "../components/ui/button";
import { openCookieBanner } from "../components/CookieBanner";

export default function CookiePolicy() {
  return (
    <LegalLayout title="Política de Cookies">
      <p className="lead text-base">
        Esta política describe qué son las cookies, cuáles utiliza {CLINIC.name} en{" "}
        <b>{CLINIC.domain}</b> y cómo puedes gestionarlas. Cumple con la LSSICE (Art. 22),
        RGPD, LOPDGDD y las directrices de la AEPD (Guía sobre el uso de las cookies, 2023).
      </p>

      <div className="not-prose my-6 rounded-lg border border-primary/30 bg-primary/5 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-semibold text-primary">Gestionar mi consentimiento</div>
            <div className="text-sm text-muted-foreground">
              Puedes revocar o modificar tu decisión en cualquier momento (RGPD Art. 7.3).
            </div>
          </div>
          <Button onClick={openCookieBanner} data-testid="reopen-cookie-banner">
            Reconfigurar cookies
          </Button>
        </div>
      </div>

      <h2>1. ¿Qué es una cookie?</h2>
      <p>
        Una cookie es un pequeño fichero de texto que un sitio web almacena en el navegador del
        usuario para recordar información sobre la visita. También aplicamos esta política a otras
        tecnologías equivalentes como <em>localStorage</em>, <em>sessionStorage</em> y píxeles.
      </p>

      <h2>2. Cookies que utilizamos</h2>
      <div className="not-prose overflow-x-auto my-4">
        <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-3 font-semibold">Nombre</th>
              <th className="text-left p-3 font-semibold">Finalidad</th>
              <th className="text-left p-3 font-semibold">Duración</th>
              <th className="text-left p-3 font-semibold">Categoría</th>
              <th className="text-left p-3 font-semibold">Base legal</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            <tr>
              <td className="p-3 font-mono text-xs">access_token</td>
              <td className="p-3">Mantener la sesión autenticada tras login</td>
              <td className="p-3">24 h</td>
              <td className="p-3"><span className="text-emerald-700">Necesaria</span></td>
              <td className="p-3 text-xs">Interés legítimo (seguridad)</td>
            </tr>
            <tr>
              <td className="p-3 font-mono text-xs">cookie_consent_v1</td>
              <td className="p-3">Guardar tu elección sobre este banner</td>
              <td className="p-3">12 meses</td>
              <td className="p-3"><span className="text-emerald-700">Necesaria</span></td>
              <td className="p-3 text-xs">Obligación legal (LSSICE)</td>
            </tr>
            <tr>
              <td className="p-3 font-mono text-xs">token</td>
              <td className="p-3">Enviar autenticación a la API (localStorage)</td>
              <td className="p-3">24 h</td>
              <td className="p-3"><span className="text-emerald-700">Necesaria</span></td>
              <td className="p-3 text-xs">Interés legítimo</td>
            </tr>
            <tr>
              <td className="p-3 font-mono text-xs">language</td>
              <td className="p-3">Recordar tu idioma preferido (ES/FR/HT)</td>
              <td className="p-3">6 meses</td>
              <td className="p-3"><span className="text-amber-700">Preferencias</span></td>
              <td className="p-3 text-xs">Consentimiento</td>
            </tr>
            <tr>
              <td className="p-3 font-mono text-xs">ai_chat_consent</td>
              <td className="p-3">Confirmar aceptación del disclaimer del chat con IA</td>
              <td className="p-3">6 meses</td>
              <td className="p-3"><span className="text-amber-700">Preferencias</span></td>
              <td className="p-3 text-xs">Consentimiento (AI Act)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>3. Cookies de terceros</h2>
      <ul>
        <li>
          <b>Stripe</b> (proveedor de pagos): cookies de sesión estrictamente necesarias para
          procesar tu pago cuando accedes a Stripe Checkout. Consulta la{" "}
          <a href="https://stripe.com/es/privacy" target="_blank" rel="noopener noreferrer">
            política de privacidad de Stripe
          </a>.
        </li>
        <li>
          <b>Jitsi Meet</b> (videoconsulta): cookies necesarias para el funcionamiento de la
          sala virtual durante la teleconsulta.
        </li>
      </ul>

      <h2>4. Analítica</h2>
      <p>
        <b>Actualmente NO utilizamos herramientas de analítica de terceros</b> (Google Analytics,
        Meta Pixel, etc.). Si en el futuro se activan, se solicitará tu consentimiento explícito
        y se detallarán aquí antes de su despliegue.
      </p>

      <h2>5. Gestión y revocación del consentimiento</h2>
      <p>Puedes:</p>
      <ul>
        <li>
          Usar el botón <b>«Reconfigurar cookies»</b> al inicio de esta página para reabrir el
          panel y modificar tus preferencias.
        </li>
        <li>
          Borrar las cookies desde la configuración de tu navegador (
          <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Chrome</a>,{" "}
          <a href="https://support.mozilla.org/kb/borrar-cookies-datos-sitio-firefox" target="_blank" rel="noopener noreferrer">Firefox</a>,{" "}
          <a href="https://support.apple.com/es-es/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a>,{" "}
          <a href="https://support.microsoft.com/help/17442" target="_blank" rel="noopener noreferrer">Edge</a>).
        </li>
        <li>
          Escribirnos a <a href={`mailto:${CLINIC.email}`}>{CLINIC.email}</a> para ejercer tus
          derechos RGPD.
        </li>
      </ul>

      <h2>6. Actualizaciones</h2>
      <p>
        Esta política se revisa periódicamente. La última actualización se registra al pie de
        cada página legal. Guardamos un histórico versionado de cada aceptación para prueba
        documental ante la AEPD.
      </p>
    </LegalLayout>
  );
}
